/**
 * TriggersSection — template-centric trigger-rule list for use inside
 * ReplayTemplateDialog (Step 03 of the mockup, ADR 0018 Q3).
 *
 * Lists every TriggerRule pointing at the given templateId, across all
 * connected repos and providers. Mirrors the repo-centric view in
 * TriggerRulesForRepo but shows the repo as the "other side" of the
 * binding (event pill + repo link + branch description + edit/delete).
 *
 * Only Replay templates get this section (Q7, ADR 0018). The caller
 * (ReplayTemplateDialog) is responsible for only mounting this component
 * for replay-kind templates — the component itself doesn't enforce that.
 *
 * Empty state: dashed-border callout per mockup Step 03.
 */
import { useState, useEffect, useCallback } from 'react'
import { Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui'
import { TriggerRuleEditorModal } from '@/components/TriggerRuleEditorModal'
import { useConfirm } from '@/hooks/useConfirm'
import { useToast } from '@/hooks/useToast'
import { toUserMessage, extractRawDetails } from '@/lib/userMessage'
import { triggerRulesAPI } from '@/lib/api'
import { eventPillClass, eventPillLabel, ruleEventDescription } from '@/lib/triggerRules'
import type { TriggerRule } from '@/types'

// ── Props ─────────────────────────────────────────────────────────────────

interface TriggersSectionProps {
  /** The templateId to query rules for. */
  templateId: string
  /** The template's display name — passed as presetTemplate to the editor. */
  templateName: string
}

// ── Component ─────────────────────────────────────────────────────────────

export function TriggersSection({ templateId }: TriggersSectionProps) {
  // #1148: shared themed confirm, replacing native window.confirm().
  const confirm = useConfirm()
  // #1149: shared themed toast, replacing native window.alert().
  const toast = useToast()
  const [rules, setRules] = useState<TriggerRule[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [editingRule, setEditingRule] = useState<TriggerRule | null | undefined>(undefined)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await triggerRulesAPI.listForTemplate(templateId)
      setRules(data)
    } catch (err) {
      setError(
        toUserMessage(
          err,
          "Couldn't load the trigger rules. Refresh — if the problem continues, check your connection.",
        ),
      )
    } finally {
      setLoading(false)
    }
  }, [templateId])

  useEffect(() => {
    void load()
  }, [load])

  function openEditModal(rule: TriggerRule) {
    setEditingRule(rule)
    setIsModalOpen(true)
  }

  function closeModal() {
    setIsModalOpen(false)
    setEditingRule(undefined)
  }

  function handleSaved(saved: TriggerRule) {
    closeModal()
    setRules((prev) => {
      const idx = prev.findIndex((r) => r.id === saved.id)
      if (idx >= 0) {
        const next = [...prev]
        next[idx] = saved
        return next
      }
      return [saved, ...prev]
    })
  }

  async function handleDelete(rule: TriggerRule) {
    const ok = await confirm({
      title: 'Delete this trigger rule?',
      description: 'The rule will no longer fire on matching repository events.',
      confirmLabel: 'Delete',
      destructive: true,
    })
    if (!ok) return
    setDeletingId(rule.id)
    try {
      await triggerRulesAPI.delete(rule.id)
      setRules((prev) => prev.filter((r) => r.id !== rule.id))
    } catch (err) {
      toast({
        title: 'Failed to delete trigger rule',
        description: toUserMessage(
          err,
          "Couldn't delete the trigger rule. Try again — if the problem continues, check your connection.",
        ),
        rawDetails: extractRawDetails(err) ?? undefined,
        variant: 'error',
      })
    } finally {
      setDeletingId(null)
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Section header */}
      <div className="mb-2 border-b border-border pb-1">
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          Triggers — when this template fires
        </p>
      </div>

      {loading && (
        <div className="space-y-1.5">
          {[1, 2].map((i) => (
            <div key={i} className="h-8 rounded bg-muted animate-pulse" />
          ))}
        </div>
      )}

      {!loading && error && (
        <p className="text-xs text-destructive">{error}</p>
      )}

      {!loading && !error && rules.length === 0 && (
        /* Empty state: dashed-border callout. Post-#1174 (meta #1172)
         * every rule is a deployment_status rule, so the empty-state
         * copy names what binding the customer would create rather
         * than generic "no triggers configured." */
        <div className="rounded-md border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
          <p>No triggers configured. This template only fires manually, from a customer CI YAML, or once a trigger rule is bound to a deployment event from your repo.</p>
        </div>
      )}

      {!loading && !error && rules.length > 0 && (
        <ul className="space-y-1.5">
          {rules.map((rule) => (
            <li
              key={rule.id}
              className="grid items-center gap-3 rounded border border-border px-2 py-1.5 text-xs"
              style={{ gridTemplateColumns: 'auto 1fr auto' }}
            >
              {/* Event pill */}
              <span
                className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${eventPillClass(rule)}`}
              >
                {eventPillLabel(rule)}
              </span>

              {/* Description — repo link + event */}
              <span className="truncate">
                <span className="font-mono text-primary">{rule.repoIdentifier}</span>{' '}
                · {ruleEventDescription(rule)}
              </span>

              {/* Actions */}
              <span className="flex gap-0.5">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => openEditModal(rule)}
                  title="Edit"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => void handleDelete(rule)}
                  disabled={deletingId === rule.id}
                  title="Delete"
                  className="text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Create hint — new rules must be added from the repo side in
          Workspace Settings because the create path requires an
          integrationId (a GitHub App installation record). The modal
          supports editing existing rules from this surface. */}
      <p className="mt-2 text-[10px] text-muted-foreground">
        To add a rule, open{' '}
        <span className="font-medium text-foreground">Workspace Settings → GitHub</span>
        {' '}and use "+ Add trigger rule" from the repo you want to monitor.
      </p>

      {/* Editor modal — only opens for EDIT (existing rules). The add
          flow above directs users to the repo-centric surface instead. */}
      {isModalOpen && editingRule && (
        <TriggerRuleEditorModal
          initialRule={editingRule}
          onClose={closeModal}
          onSaved={handleSaved}
        />
      )}
    </div>
  )
}
